import React from 'react';
import styled from 'styled-components';
import { StyledH2 } from 'components';
import { useSelector } from 'react-redux';
import { TrackingProp } from 'react-tracking';
import GlobalLayout from './GlobalLayout';
import BreadCrumbs from '../Breadcrumbs';
import { customerSelector } from 'store/customer/selectors';
import { isNotServerSideRendering } from 'helpers/env';
import SideMenu from '../SideMenu';
import { ADDRESS_BOOK, ADDRESS_LISTS, AccountSectionPages } from '../../constants/navigation';
import { useSplitTreatment } from 'hooks/splits/useSplitTreatment';
import { SPLIT_TRAFFIC_TYPES } from 'au-js-sdk/lib/services/split.io/constants';

const SPLIT_IO_FEATURE_FLAG_CARD_ADDRESSING = 'FeatureFlag-CardAddressing';
const SPLIT_IO_FEATURE_FLAG_CARD_ADDRESSING_COOKIE = 'au_split_feature_flag_card_addressing';

const StyledBreadCrumbs = styled(BreadCrumbs)`
  margin: 24px 0;
`;

const HeaderWrapper = styled.div`
  ${StyledH2} {
    padding: 0;
  }
`;

interface AccountLayoutProps {
  children: React.ReactNode;
  tracking: TrackingProp;
  hideSideMenuAt?: number;
}

const AccountWrapper = styled.div`
  h2 {
    padding: 2rem 0;
  }
`;

const AccountContent = styled.div<{ hideSideMenuAt?: number }>`
  display: flex;
  border-top: 1px#E9E9E8 solid;
  padding-top: 1.6rem;
  h2 {
    padding: 0;
  }

  ${({ hideSideMenuAt = 767 }) =>
    `@media screen and (max-width: ${hideSideMenuAt}px) {
      flex-direction: column-reverse;
    }`}
`;

const AccountLayout = ({ children, tracking, hideSideMenuAt }: AccountLayoutProps): React.JSX.Element => {
  const customer = useSelector(customerSelector);

  const { treatmentStatus: displayAddressManagementPage } = useSplitTreatment(
    SPLIT_IO_FEATURE_FLAG_CARD_ADDRESSING,
    SPLIT_IO_FEATURE_FLAG_CARD_ADDRESSING_COOKIE,
    SPLIT_TRAFFIC_TYPES.MAGENTO
  );

  const getGreetingsMessage = () => {
    if (isNotServerSideRendering()) {
      if (document.title !== 'My Account') {
        return document.title;
      } else {
        return (customer && customer.firstname !== null && `Hello, ${customer.firstname}`) || 'Hello!';
      }
    }
    return 'Hello!';
  };

  const getCurrentAccountPage = () => {
    let currentPage = { name: 'My Account', urlPath: '/customer/account', isGatsbyPage: false };
    if (isNotServerSideRendering()) {
      const pathPage = AccountSectionPages[window.location.pathname];
      if (pathPage && pathPage.name && pathPage.urlPath) {
        currentPage = AccountSectionPages[window.location.pathname];
      }
    }

    return currentPage;
  };

  const getBreadCrumbs = () => {
    const breadCrumbs = [
      { name: 'HOME', urlPath: '/' },
      { name: 'ACCOUNT', urlPath: '/customer/account' }
    ];
    if (isNotServerSideRendering()) {
      if (window.location.pathname !== '/customer/account') {
        breadCrumbs.push({ name: getCurrentAccountPage().name.toUpperCase(), urlPath: window.location.pathname });
      } else {
        breadCrumbs.push({ name: 'ACCOUNT DASHBOARD', urlPath: '/customer/account' });
      }
    }
    return breadCrumbs;
  };

  const getAccountMenuOptions = () => {
    const childrenOptions = Object.keys(AccountSectionPages).map(page => ({
      name: AccountSectionPages[page].name.toUpperCase(),
      path: AccountSectionPages[page].urlPath,
      isGatsbyPage: AccountSectionPages[page].isGatsbyPage
    }));

    // if/when this split is removed and we decide to stick with the new address lists
    // section we should remove anything /account/address-book/ from src/constants/navigation
    if (displayAddressManagementPage) {
      const addressBookSectionName = ADDRESS_BOOK.name.toUpperCase();
      const addressBookPosition = childrenOptions.findIndex(child => child.name === addressBookSectionName);
      childrenOptions.splice(addressBookPosition, 1);
    }

    if (!displayAddressManagementPage) {
      const addressListSectionName = ADDRESS_LISTS.name.toUpperCase();
      const addressListPosition = childrenOptions.findIndex(child => child.name === addressListSectionName);
      childrenOptions.splice(addressListPosition, 1);
    }

    return [{ name: getCurrentAccountPage().name, children: childrenOptions }];
  };

  return (
    <GlobalLayout tracking={tracking} displayPromoBanner contained>
      <AccountWrapper className="AccountLayout">
        <HeaderWrapper>
          <StyledH2 secondary>{getGreetingsMessage()}</StyledH2>
        </HeaderWrapper>
        <StyledBreadCrumbs pages={getBreadCrumbs()} />
        <AccountContent hideSideMenuAt={hideSideMenuAt}>
          <SideMenu
            sideMenu={getAccountMenuOptions()}
            className="account-layout"
            addLogout
            displaySectionTitles={false}
          />
          {children}
        </AccountContent>
      </AccountWrapper>
    </GlobalLayout>
  );
};

export default AccountLayout;
